import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { usePusher } from '../contexts/PusherContext';
import {
  AgentEvents,
  ThreadActivityUpdateEvent,
  UnreadThread,
  GetUnreadThreadsResponse,
  MarkThreadsAsReadRequest,
} from '@dreamai/types';
import api from '../api';

interface ThreadReadsState {
  threads: UnreadThread[];
  totalCount: number;
  otherUnreadCount: number;
  countByAgent: Map<string, number>;
  threadIdsByAgent: Map<string, string[]>;
  loading: boolean;
  lastUpdated: number;
}

interface ThreadReadsContextType {
  totalUnreadCount: number;
  otherUnreadCount: number;
  getUnreadCountByAgent: (agentId: string) => number;
  getUnreadThreadIdsByAgent: (agentId: string) => string[];
  getAllUnreadThreads: () => UnreadThread[];
  refreshUnreads: () => Promise<void>;
  markThreadAsRead: (threadId: string | string[]) => Promise<void>;
  loading: boolean;
}

const ThreadReadsContext = createContext<ThreadReadsContextType | null>(null);

interface ThreadReadsProviderProps {
  children: React.ReactNode;
}

export const ThreadReadsProvider: React.FC<ThreadReadsProviderProps> = ({
  children,
}) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const pusherContext = usePusher(true);
  const companyChannel = pusherContext?.pusherChannel;
  const [state, setState] = useState<ThreadReadsState>({
    threads: [],
    totalCount: 0,
    otherUnreadCount: 0,
    countByAgent: new Map(),
    threadIdsByAgent: new Map(),
    loading: true,
    lastUpdated: 0,
  });
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Process thread data and build per-agent maps
   */
  const processThreads = (
    threads: UnreadThread[],
    otherUnreadCount: number,
  ): ThreadReadsState => {
    const countByAgent = new Map<string, number>();
    const threadIdsByAgent = new Map<string, string[]>();

    threads.forEach((thread) => {
      if (thread.agent_id) {
        // Update count
        const currentCount = countByAgent.get(thread.agent_id) || 0;
        countByAgent.set(thread.agent_id, currentCount + 1);

        // Update thread IDs
        const currentThreads = threadIdsByAgent.get(thread.agent_id) || [];
        threadIdsByAgent.set(thread.agent_id, [
          ...currentThreads,
          thread.thread_id,
        ]);
      }
    });

    return {
      threads,
      totalCount: threads.length,
      otherUnreadCount,
      countByAgent,
      threadIdsByAgent,
      loading: false,
      lastUpdated: Date.now(),
    };
  };

  /**
   * Fetch unread threads from the API
   */
  const fetchUnreadThreads = useCallback(async () => {
    try {
      const accessToken = await getAccessTokenSilently();

      const response = await api.get<GetUnreadThreadsResponse>(
        '/thread-reads/unread',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (response.data.success) {
        const newState = processThreads(
          response.data.unread_threads,
          response.data.other_unread_count,
        );

        // Only update state if data actually changed
        setState((prev) => {
          // Always update on first load
          if (prev.lastUpdated === 0) {
            return newState;
          }

          // Check if total count changed
          if (prev.totalCount !== newState.totalCount) {
            return newState;
          }

          // Check if other count changed
          if (prev.otherUnreadCount !== newState.otherUnreadCount) {
            return newState;
          }

          // Check if per-agent counts changed
          const prevCounts = JSON.stringify(
            Array.from(prev.countByAgent).sort(),
          );
          const newCounts = JSON.stringify(
            Array.from(newState.countByAgent).sort(),
          );
          if (prevCounts !== newCounts) {
            return newState;
          }

          // No changes detected, keep previous state
          return prev;
        });
      } else {
        console.error('Failed to fetch unread threads:', response.data.error);
        setState((prev) => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error fetching unread threads:', error);
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [getAccessTokenSilently]);

  /**
   * Mark thread(s) as read
   */
  const markThreadAsRead = useCallback(
    async (threadId: string | string[]) => {
      try {
        const accessToken = await getAccessTokenSilently();

        const requestBody: MarkThreadsAsReadRequest = {
          threadIds: threadId,
        };

        await api.post('/thread-reads/mark-read', requestBody, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        // Refresh unreads after marking as read
        await fetchUnreadThreads();
      } catch (error) {
        console.error('Error marking thread as read:', error);
      }
    },
    [getAccessTokenSilently, fetchUnreadThreads],
  );

  /**
   * Debounced refresh to avoid hammering the API
   */
  const debouncedRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    refreshTimeoutRef.current = setTimeout(() => {
      fetchUnreadThreads();
    }, 500); // 500ms debounce
  }, [fetchUnreadThreads]);

  /**
   * Initial fetch on mount
   */
  useEffect(() => {
    fetchUnreadThreads();
  }, [fetchUnreadThreads]);

  /**
   * Listen for thread activity updates via Pusher
   */
  useEffect(() => {
    if (!companyChannel || !user) return;

    const handleThreadActivity = (data: ThreadActivityUpdateEvent) => {
      // Thread activity detected - refresh unreads
      console.log('here');
      debouncedRefresh();
    };

    companyChannel.bind(
      AgentEvents.THREAD_ACTIVITY_UPDATE,
      handleThreadActivity,
    );

    return () => {
      companyChannel.unbind(
        AgentEvents.THREAD_ACTIVITY_UPDATE,
        handleThreadActivity,
      );
    };
  }, [companyChannel, user, debouncedRefresh]);

  /**
   * Cleanup timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  const contextValue = useMemo<ThreadReadsContextType>(
    () => ({
      totalUnreadCount: state.totalCount,
      otherUnreadCount: state.otherUnreadCount,
      getUnreadCountByAgent: (agentId: string) => {
        // Special handling for "other" - return the shared threads count
        if (agentId === 'other') {
          return state.otherUnreadCount;
        }
        return state.countByAgent.get(agentId) || 0;
      },
      getUnreadThreadIdsByAgent: (agentId: string) =>
        state.threadIdsByAgent.get(agentId) || [],
      getAllUnreadThreads: () => state.threads,
      refreshUnreads: fetchUnreadThreads,
      markThreadAsRead,
      loading: state.loading,
    }),
    [
      state.totalCount,
      state.otherUnreadCount,
      state.countByAgent,
      state.threadIdsByAgent,
      state.threads,
      state.loading,
      fetchUnreadThreads,
      markThreadAsRead,
    ],
  );

  return (
    <ThreadReadsContext.Provider value={contextValue}>
      {children}
    </ThreadReadsContext.Provider>
  );
};

/**
 * Hook to use thread reads context
 */
export const useThreadReads = (): ThreadReadsContextType => {
  const context = useContext(ThreadReadsContext);
  if (!context) {
    throw new Error('useThreadReads must be used within ThreadReadsProvider');
  }
  return context;
};
